// /src/components/nodes./featureOverlay/modules/UplynkCentralModule.js
import React, { useState, useEffect, useRef } from 'react';
import { useApi } from '../../../../context/ApiContext/ApiContext';
import { FaArrowDown, FaArrowLeft, FaArrowRight, FaArrowUp } from 'react-icons/fa';
import { copyToClipboard } from '../../../../utils/copyToClipboard';
import './UplynkCentralModule.css';

// const StatusTracker = React.forwardRef(({ 
const UplynkCentralModule = React.forwardRef((props, ref) => {
  const { 

  // DEFAULTS - Data specification
    dataPath,
    getItems,  // Alternative to dataPath - a function to get items from state
    
    // Key mappings
    keyMappings = {
      state: "state",
      progress: "progress",
      progressPercent: "progress_percent",
      timestamp: "timestamp", 
      id: "attempt"  // Optional unique identifier
    },
    
    // Visual styling
    stateStyles = {
      states: {
        ["waiting"]: { border: 'border-amber-600', progress: 'bg-amber-300 border-amber-600', text: 'text-white' },
        ["assigned"]: { border: 'border-indigo-500', progress: 'bg-[#a5b4fc] border-indigo-600', text: 'text-white' },
        ["done"]: { border: 'border-purple-600', progress: 'bg-[#8800AA] border-purple-600', text: 'text-white' },
        ["error"]: { border: 'border-red-600', progress: 'bg-[#77000011] border-red-600', text: 'text-white' },
        ["default"]: { border: 'border-gray-600', progress: 'bg-purple-800 border-purple-600', text: 'text-gray-200' }
      }
    },
    
    // Special conditions and behaviors
    conditions = [
    ],    
    
    // Group same jobState items - Rules for grouping similar updates
    groupingRules = {
      groupSameStates: true,
      groupByProperties: ["state", "progress"],
      updateProperties: ["progressPercent", "timestamp", "title"] // Add "title" here
    },

    // Visual options
    visualOptions = {
      height: "17rem",
      width: "17rem",
      borderRadius: "0.5rem",
      showLiveIndicator: true,
      scrollToLatest: false,
      scrollThreshold: 1000,
      maxItems: Infinity,
      fadeOldItems: true,
      itemOrder: "reverse", // "chronological" or "reverse"
      highlightLatestOnly: true, // Only highlight the most recent item
      compactLayout: true, // More compact spacing
      contrastRatio: 1, // How much to fade older items (0-1)
      autoScrollButton: false
    },    
    // Custom rendering overrides
    renderOverrides = {}
  } = props;
  // }) => {

  // API context
  const { state, isExecuting, isCloudProcessRunning } = useApi();
  // const [ autoScroll, setAutoScroll ] = useState(visualOptions.scrollToLatest);
  const [ autoScroll, setAutoScroll ] = useState(true);
  const scrollContainerRef = useRef(null);
  const lastItemRef = useRef(null);
  const statusTrackerRef = useRef();

  // Add after the existing state declarations (after line 97)
  // const [moduleStatus, setModuleStatus] = useState('active');
  const [moduleStatus, setModuleStatus] = useState(isExecuting || isCloudProcessRunning ? 'active' : 'idle');
  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());
  const timeoutCheckRef = useRef(null);
  const timeoutDuration = 30000; // 30 seconds timeout - adjust as needed
  
  // Get value from nested object using dot-notation path
  const getValueByPath = (obj, path) => {
  // console.log("&&& UplynkCentralModule - getValueByPath - obj", obj);
  // console.log("&&& UplynkCentralModule - getValueByPath - path", path);
    if (!path) return obj;
    const pathParts = path.split('.');
    return pathParts.reduce((acc, part) => {
      return acc && acc[part] !== undefined ? acc[part] : undefined;
    }, obj);
  };
  
  // Get the data items from jobStates
  const getDataItems = () => {
    if (getItems && typeof getItems === 'function') {
      return getItems(state);
    }
    if (dataPath) {
      return getValueByPath(state, dataPath) || [];
    }
    return [];
  };
  
  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };
  
  // Get style for a jobState item's state
  const getStateStyle = (stateValue, type = 'border') => {
    // console.log("getStateStyle - stateValue: ", stateValue);
    // console.log("getStateStyle - type: ", type);
    if (!stateValue) return stateStyles.states.default[type];
    
    try {
      // Find matching state in configuration
      for (const [states, styles] of Object.entries(stateStyles.states)) {
        if (states === 'default') continue;
        
        const stateArray = states.replace(/[\[\]]/g, '').split(',').map(s => s.trim());
        if (stateArray.includes(stateValue)) {
          return styles[type];
        }
      }
      
      // Return default if no match
      return stateStyles.states.default[type];
    } catch (error) {
      const defaultStyle = { border: 'border-gray-600', progress: 'bg-purple-800 border-purple-600', text: 'text-gray-200' }
      return defaultStyle[type];
    }
  };
  
  
  // Process and group jobState items 
  const processItems = (items) => {
    if (!items || items.length === 0) return [];
    if (!groupingRules.groupSameStates) return items;
    
    const result = [];
    let currentGroup = null;
    
    items.forEach((item) => {
      // console.log("processItems - Job State: ", item);
      
      // Should we create a new group?
      const needNewGroup = !currentGroup || groupingRules.groupByProperties.some(prop => {
        const key = keyMappings[prop] || prop;
        return currentGroup[key] !== item[key];
      });
      
      if (needNewGroup) {
        // Start a new group
        currentGroup = { ...item };
        result.push(currentGroup);
      } else {
        // Update the current group with specified properties
        groupingRules.updateProperties.forEach(prop => {
          const key = keyMappings[prop] || prop;
          currentGroup[key] = item[key];
        });
      }
    });
    
    return result;
  };

  // Process and order items based on settings
  const processAndOrderItems = (items) => {
      // First process items (grouping logic remains the same)
      const processedItems = processItems(items);
      
      // Order items based on settings
      return visualOptions.itemOrder === "reverse" 
      ? [...processedItems].reverse() 
      : processedItems;
  };
  
  
  // Check if a condition matches an item
  const checkCondition = (condition, item) => {
    // console.log("condition, item");
    // console.log(condition, item);
    if (!condition || !condition.when) return false;
    
    const { property, values, value, comparison, and, or } = condition.when;
    const key = keyMappings[property] || property;
    const itemValue = item[key];
    
    let result = false;
    
    // Check main condition
    if (values) {
      const valueArray = Array.isArray(values) ? values : [values];
      if (comparison === 'equals') {
        result = valueArray.includes(itemValue);
      } else if (comparison === 'notEquals') {
        result = !valueArray.includes(itemValue);
      } else if (comparison === 'contains') {
        result = valueArray.some(v => itemValue && itemValue.includes(v));
      }
    } else if (value !== undefined) {
      if (comparison === 'equals') {
        result = itemValue === value;
      } else if (comparison === 'notEquals') {
        result = itemValue !== value;
      } else if (comparison === 'contains') {
        result = itemValue && itemValue.includes(value);
      } else if (comparison === 'greaterThan') {
        result = itemValue > value;
      } else if (comparison === 'lessThan') {
        result = itemValue < value;
      }
    }
    
    // Check additional AND condition if specified
    if (result && and) {
      result = checkCondition({ when: and }, item);
    }
    
    // Check additional OR condition if specified
    if (!result && or) {
      result = checkCondition({ when: or }, item);
    }
    
    return result;
  };
  
  
  // Apply all matching conditions to an item
  const applyConditions = (item) => {
    let effects = {};
    
    conditions.forEach(condition => {
      if (checkCondition(condition, item)) {
        // Handle removeClasses
        if (condition.then.removeClasses) {
          effects.removeClasses = effects.removeClasses 
            ? `${effects.removeClasses} ${condition.then.removeClasses}`
            : condition.then.removeClasses;
        }
        
        // Handle status changes - TEMP CHANGE
        // if (condition.then.setStatus && moduleStatus !== condition.then.setStatus) {
        //   // Apply the status change
        //   console.log("Setting Module Status using Conditions: ", condition.then.setStatus);
        //   setModuleStatus(condition.then.setStatus);
          
        //   // Clear the timeout if we're no longer active
        //   if (condition.then.setStatus !== 'active' && timeoutCheckRef.current) {
        //     clearInterval(timeoutCheckRef.current);
        //     timeoutCheckRef.current = null;
        //   }
        // }
        // TEMP TEST
        if (condition.then.setStatus) { // Check if setStatus is even defined in the condition's 'then'
          console.log(`[applyConditions] Item causing status change: ${JSON.stringify(item, null, 2)}`);
          console.log(`[applyConditions] Matched condition: ${JSON.stringify(condition, null, 2)}`);
          console.log(`[applyConditions] Current moduleStatus: ${moduleStatus}, Condition wants to set to: ${condition.then.setStatus}`);
      
          if (moduleStatus !== condition.then.setStatus) {
              console.log(`[applyConditions] ---> EXECUTING setModuleStatus(${condition.then.setStatus})`);
              setModuleStatus(condition.then.setStatus); // This is the problematic call during render
      
              // Clear the timeout if we're no longer active
              if (condition.then.setStatus !== 'active' && timeoutCheckRef.current) {
                  clearInterval(timeoutCheckRef.current);
                  timeoutCheckRef.current = null;
              }
          } else {
              console.log(`[applyConditions] ---> Status already ${condition.then.setStatus}, not calling setModuleStatus.`);
          }
        }      
        
        // Merge remaining effects
        effects = { ...effects, ...condition.then };
      }
    });
    
    return effects;
  };  
  
  
  // Get and process data
  const rawItems = getDataItems();
  const items = processAndOrderItems(rawItems);
  
  // Find item for live updates
  const findLiveUpdateItem = () => {
    if (!rawItems.length) return null;
    
    // Find most recent item matching any condition with showLiveIndicator
    for (const condition of conditions) {
      if (condition.then?.showLiveIndicator) {
        return [...rawItems].reverse().find(item => checkCondition(condition, item));
      }
    }
    
    return null;
  };
  
  const liveUpdateItem = findLiveUpdateItem();

  // Scroll Handling
  const enableAutoScroll = () => {
    // Set auto-scroll state to true
    setAutoScroll(true);
    
    // Immediately scroll to bottom
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  };


  const hasPlayingItem = () => {
    const stateKey = keyMappings.state;
    return rawItems.some(item => item[stateKey] === "playing");
  };


  // useEffect(() => {
  //   console.log("isExecuting, isCloudProcessRunning", isExecuting, isCloudProcessRunning);
  //   console.log("isExecuting || isCloudProcessRunning", isExecuting || isCloudProcessRunning);
  //   console.log("isExecuting || isCloudProcessRunning || hasPlayingItem()", hasPlayingItem());
  //   if (isExecuting || isCloudProcessRunning || !hasPlayingItem()) {
  //     setModuleStatus('active');
  //   } else {
  //     setModuleStatus('inactive');
  //   }
  //   console.log("----- module status - isExecuting: ", isExecuting);
  // }, [isExecuting, isCloudProcessRunning, rawItems])
  useEffect(() => {
    let newStatus = 'idle'; // Default status
  
    const hasErrorItem = rawItems.some(item => item[keyMappings.state] === 'error'); // Assuming 'error' state exists
    const isAnyPlaying = rawItems.some(item => item[keyMappings.state] === 'playing');
    // Check for a 'done' state that signifies overall completion, if applicable.
    // This might need more complex logic depending on your definition of "all done".
    const allRelevantDone = rawItems.length > 0 && rawItems.every(item => item[keyMappings.state] === 'done' || item[keyMappings.state] === 'complete_custom_state');
  
  
    if (hasErrorItem) {
      newStatus = 'canceled'; // Or 'error'
    } else if (isExecuting || isCloudProcessRunning) {
      newStatus = 'active';
    } else if (isAnyPlaying) {
      newStatus = 'active'; // Or 'playing'
    } else if (allRelevantDone && !isAnyPlaying && !isExecuting && !isCloudProcessRunning) { // Ensure nothing is active or playing
      newStatus = 'complete';
    }
    // Add other conditions as needed (e.g., for 'idle')
  
    if (moduleStatus !== newStatus) {
      console.log(`[useEffect moduleStatus] Deciding moduleStatus. isExecuting: ${isExecuting}, isCloudProcessRunning: ${isCloudProcessRunning}, hasError: ${hasErrorItem}, isAnyPlaying: ${isAnyPlaying}, allDone: ${allRelevantDone}. Old: ${moduleStatus}, New: ${newStatus}`);
      setModuleStatus(newStatus);
    }
  }, [isExecuting, isCloudProcessRunning, rawItems, keyMappings.state, moduleStatus]); // Add moduleStatus to deps
 

  // Auto-scroll when new updates arrive
  useEffect(() => {
    // Always scroll when items change
    if (scrollContainerRef.current && rawItems.length > 0) {
      // Use a timeout to ensure DOM has fully updated
      setTimeout(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
          // Use smooth scrolling behavior
          scrollContainer.scrollTo({
            top: scrollContainer.scrollHeight,
            behavior: 'smooth'
          });
        }
      }, 100); // Adjust timing as needed
    }
  }, [rawItems]); // Only depend on rawItems changing
  
  // TIMEOUT
  useEffect(() => {
    console.log("moduleStatus", moduleStatus);
    // Only set up the interval if we're in active state
    if (moduleStatus !== 'active') {
      // Clear any existing interval when we're not active
      if (timeoutCheckRef.current) {
        clearInterval(timeoutCheckRef.current);
        timeoutCheckRef.current = null;
      }
      return;
    }
    
    const checkForTimeout = () => {
      const currentTime = Date.now();
      // console.log("currentTime", currentTime);
      // console.log("lastUpdateTime", lastUpdateTime);
      // console.log("currentTime - lastUpdateTime", currentTime - lastUpdateTime);
      
      if (currentTime - lastUpdateTime > timeoutDuration) {
        // Clear the interval immediately when timing out
        clearInterval(timeoutCheckRef.current);
        timeoutCheckRef.current = null;
        
        // Update status
        setModuleStatus('idle');
        console.log('Status tracker timed out due to inactivity');
      }
    };
    
    // Set up interval only when active
    timeoutCheckRef.current = setInterval(checkForTimeout, 5000);
    console.log("timeoutCheckRef.current", timeoutCheckRef.current);
    
    // Clear interval on unmount or when dependencies change
    return () => {
      if (timeoutCheckRef.current) {
        clearInterval(timeoutCheckRef.current);
      }
    };
  }, [moduleStatus, lastUpdateTime, timeoutDuration]);
  
  // Add data change detection to update lastUpdateTime
  useEffect(() => {
    console.log("rawItems", rawItems);
    if (rawItems.length > 0) {
      setLastUpdateTime(Date.now());
      // If we were idle and got new updates, go back to active
      if (moduleStatus === 'idle') {
        setModuleStatus('active');
      }
    }
  }, [rawItems]);

  // useEffect(() => {
  //   console.log("@@@ State: ", state);
  // }, [state]);


  
  // Expose methods for parent component using React forwardRef/useImperativeHandle
  // React.useImperativeHandle(ref, () => ({
  //   forceComplete: () => {
  //     setModuleStatus('complete');
  //     // Stop checking for timeouts
  //     if (timeoutCheckRef.current) {
  //       clearInterval(timeoutCheckRef.current);
  //     }
  //     // Show completion notification
  //     // toast.success('Process marked as complete');
  //   },
  //   forceCancel: () => {
  //     setModuleStatus('canceled');
  //     // Stop checking for timeouts
  //     if (timeoutCheckRef.current) {
  //       clearInterval(timeoutCheckRef.current);
  //     }
  //     // Show cancellation notification
  //     // toast.warning('Process canceled');
  //   },
  //   resetStatus: () => {
  //     setModuleStatus('active');
  //     setLastUpdateTime(Date.now());
  //     // toast.info('Process reactivated');
  //   },
  //   getStatus: () => moduleStatus
  // }));

  // When needing to force cancel
  const handleReset = () => {
    // statusTrackerRef.current.forceCancel();
    setModuleStatus('canceled');
    // Stop checking for timeouts
    if (timeoutCheckRef.current) {
      clearInterval(timeoutCheckRef.current);
    }
  };
  

  // Render a single item
  const renderItem = (item, index, isLatest) => {
    // console.log("renderItem - item: ", item);
    // console.log("renderItem - index: ", index);
    // console.log("renderItem - isLatest: ", isLatest);
    if (renderOverrides.item && typeof renderOverrides.item === 'function') {
      return renderOverrides.item({
        item,
        index,
        isLatest,
        formatTime,
        getStateStyle,
        keyMappings,
        applyConditions,
        liveUpdateItem
      });
    }
    
    // Default rendering
    const stateKey = keyMappings.state;
    const progressKey = keyMappings.progress;
    const progressPercentKey = keyMappings.progressPercent;
    const timestampKey = keyMappings.timestamp;
    
    const stateValue = item[stateKey];
    const progressText = item[progressKey] || 'No progress info';
    let progressPercent = item[progressPercentKey] || 0;
    
    // Apply any matching conditions
    const effects = applyConditions(item);
    const isLiveUpdate = liveUpdateItem && 
      effects.showLiveIndicator && 
      item[stateKey] === liveUpdateItem[stateKey] && 
      item[progressKey] === liveUpdateItem[progressKey];
    
    // Use latest progress for live updates
    if (isLiveUpdate && liveUpdateItem) {
      progressPercent = liveUpdateItem[progressPercentKey] || 0;
    }

    // Determine if this item should be highlighted
    const isHighlighted = visualOptions.highlightLatestOnly 
      ? isLatest 
      : true;

    // Apply appropriate styling based on highlight status
    const itemOpacity = isHighlighted
      ? 1 
      : visualOptions.contrastRatio;

    
    return (
      <div 
        key={index} 
        ref={index === 0 && visualOptions.itemOrder === "reverse" ? lastItemRef : null}
        className={`
          ${index === items.length - 1 && !isLiveUpdate ? '' : ''}
          ${isHighlighted ? (moduleStatus === 'active' ? 'active-item-pulse' : '') + 'bg-[#00000099]' : 'bg-[#1f2937dd]'}
          ${!isHighlighted ? 'text-gray-400' : 'text-white'}
          w-full px-1 pt-1.5 pb-1.5 transition-all duration-300 transform first:border-t border-b
          ${getStateStyle(stateValue, 'border')}
          shadow-lg
          ${effects.finalClasses || ''}
        `}
        title={progressText || ''}
        onClick={(e) => progressText && copyToClipboard(progressText, e)}
      >

        <div className="flex justify-between">
          {/* <span className="text-xs font-bold pb-0.5">{stateValue === "default" ? item['title'] : item[stateKey]}</span> */}
          {/* <span className="text-[0.8em] font-mono font-bold pb-0.5">{item?.title || stateValue}</span> */}
          <span className="text-[0.75em] font-mono font-bold pb-0.5">
            {item?.title 
              ? (item?.title.length > 14 ? `${item?.title.substring(0, 15)}` : item?.title) 
              : stateValue}
          </span>
          <span className={`font-mono text-[0.6em] ${isHighlighted ? 'text-gray-200' : 'text-gray-400'}`}>{formatTime(item[timestampKey])}</span>
        </div>
        
        <div className={`text-[0.7em] mt-0 truncate ${isHighlighted ? 'text-gray-200' : 'text-gray-300'}`}>
          {progressText}
          {isLiveUpdate && progressPercent > 0 && (
            <span className="ml-1 text-[0.9em] font-medium">({progressPercent}%)</span>
          )}
        </div>
        
        {renderProgressBar(item, stateValue, progressPercent, effects, isLiveUpdate)}
      </div>
    );
  };
  
  // Render progress bar
  const renderProgressBar = (item, stateValue, progressPercent, effects, isLiveUpdate) => {
    if (renderOverrides.progressBar && typeof renderOverrides.progressBar === 'function') {
      return renderOverrides.progressBar({
        item,
        stateValue,
        progressPercent,
        effects,
        isLiveUpdate,
        getStateStyle
      });
    }
    
    return (
      <div className="w-full h-1 bg-gray-600 bg-opacity-50 rounded-full mt-1 overflow-hidden">
        <div 
          className={`h-full transition-all duration-500 ease-out ${getStateStyle(stateValue, 'progress')}`}
          style={{ 
            width: `${progressPercent}%`,
            transition: isLiveUpdate ? 'width 0.5s ease-out' : 'none', 
            opacity: visualOptions.contrastRatio
          }}
        ></div>
      </div>
    );
  };
  
  if (items.length === 0) return null;

  // MAIN RING
  return (
    <>
      <div 
        className="relative flex flex-col justify-center items-center rounded-full"
        style={{
          height: visualOptions.height,
          width: visualOptions.width,
          // background: 'radial-gradient(circle, rgba(17, 24, 39, 0.7) 100%, rgba(17, 24, 39, 1.1) 10%)',
          // backdropFilter: 'blur(6px)',
        }}
      >
        {visualOptions.autoScrollButton &&
          <button 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              enableAutoScroll();
            }}
            className='absolute top-[50%] -mt-2.5 right-[10%] p-0.5 z-[1000] text-gray-600 border border-gray-700 rounded-full hover:text-gray-300'>
            <FaArrowDown />
          </button>
        }

          <button className='text-gray-800 hover:text-gray-600 bottom-0 absolute cursor-pointer z-[1000]' title='Reset State'
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleReset()
            }}
          >
            x
          </button>

        <div 
          ref={scrollContainerRef}
          className={`relative overflow-y-auto flex-grow scrollbar-inside my-6 w-[10.3rem] max-h-[${visualOptions.height * 0.8}]`}
          style={{ 
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* <div className={`flex flex-col justify-center items-center flex-grow backdrop-blur-sm transition-all duration-300`}> */}
          <div className={`flex flex-col justify-center items-center flex-grow backdrop-blur-sm transition-[backdrop-filter] duration-1000 nopan nowheel nodrag`}>
            {items.slice(0, visualOptions.maxItems).map((item, index) => 
              renderItem(item, index, index === items.length - 1)
            )}
          </div>
        </div>

        
        {/* Live update indicator */}
        {liveUpdateItem && visualOptions.showLiveIndicator && (
          <div className="absolute bottom-2 right-2 flex items-center">
            <div className="h-2 w-2 rounded-full bg-indigo-400 mr-1 animate-pulse"></div>
            <span className="text-xs text-indigo-300">Live</span>
          </div>
        )}
      </div>
      

    </>
  );
});

export default UplynkCentralModule;